
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useHistoryOperations } from "@/hooks/useHistoryOperations";

export interface CompanyData {
  id: string;
  name: string;
  street?: string | null;
  postal_code?: string | null;
  city?: string | null;
  country?: string | null;
  assignee?: string | null;
  notes?: string | null;
  vat_number?: string | null;
  logo_url?: string | null;
}

// Update the interface to accept either a refresh function or an object containing it
type RefreshFunctionParam = (() => void) | { fetchCompanies: () => void } | { getCompanies: () => void };

export const useOrganizationManagement = (refreshData: RefreshFunctionParam) => {
  const { logHistory } = useHistoryOperations('company');
  
  // Extract the refresh function regardless of the format it was passed in
  const getRefreshFunction = () => {
    if (typeof refreshData === 'function') {
      return refreshData;
    } else if ('fetchCompanies' in refreshData) {
      return refreshData.fetchCompanies;
    } else if ('getCompanies' in refreshData) {
      return refreshData.getCompanies;
    }
    return () => console.warn('No valid refresh function provided');
  };
  
  const handleEditCompany = async (companyData: CompanyData) => {
    try {
      console.log("Updating company:", companyData);
      
      // First get the current company data to compare for history logging
      const { data: currentCompany, error: fetchError } = await supabase
        .from('companies')
        .select('*')
        .eq('id', companyData.id)
        .single();
        
      if (fetchError) {
        console.error("Error fetching current company data:", fetchError);
        throw fetchError;
      }
      
      // Create an update object with only the fields that are provided
      const updateData: any = {};
      
      // Always include name as it's required
      updateData.name = companyData.name;
      
      // Add optional fields if they exist
      if (companyData.street !== undefined) updateData.street = companyData.street;
      if (companyData.postal_code !== undefined) updateData.postal_code = companyData.postal_code;
      if (companyData.city !== undefined) updateData.city = companyData.city;
      if (companyData.country !== undefined) updateData.country = companyData.country;
      if (companyData.assignee !== undefined) updateData.assignee = companyData.assignee;
      if (companyData.notes !== undefined) updateData.notes = companyData.notes;
      if (companyData.vat_number !== undefined) updateData.vat_number = companyData.vat_number;
      if (companyData.logo_url !== undefined) updateData.logo_url = companyData.logo_url;
      
      const { error } = await supabase
        .from('companies')
        .update(updateData)
        .eq('id', companyData.id);
      
      if (error) {
        console.error("Error updating company:", error);
        throw error;
      }
      
      // Log changes to history for each field that changed
      if (currentCompany) {
        // Log name change if it changed
        if (currentCompany.name !== companyData.name) {
          await logHistory({
            entityId: companyData.id,
            entityType: "company",
            action: "updated",
            description: "Company Name",
            change: {
              field: "Company Name",
              oldValue: currentCompany.name,
              newValue: companyData.name
            }
          });
        }
        
        // Log other field changes
        if (companyData.street !== undefined && currentCompany.street !== companyData.street) {
          await logHistory({
            entityId: companyData.id,
            entityType: "company",
            action: "updated",
            description: "Company street address",
            change: {
              field: "Company street address",
              oldValue: currentCompany.street,
              newValue: companyData.street
            }
          });
        }
        
        if (companyData.notes !== undefined && currentCompany.notes !== companyData.notes) {
          await logHistory({
            entityId: companyData.id,
            entityType: "company",
            action: "updated",
            description: "Company notes",
            change: {
              field: "Company notes",
              oldValue: currentCompany.notes,
              newValue: companyData.notes
            }
          });
        }
        
        // Add more specific field logging for other fields
        if (companyData.city !== undefined && currentCompany.city !== companyData.city) {
          await logHistory({
            entityId: companyData.id,
            entityType: "company",
            action: "updated",
            description: "City",
            change: {
              field: "City",
              oldValue: currentCompany.city,
              newValue: companyData.city
            }
          });
        }
        
        if (companyData.postal_code !== undefined && currentCompany.postal_code !== companyData.postal_code) {
          await logHistory({
            entityId: companyData.id,
            entityType: "company",
            action: "updated",
            description: "Postal code",
            change: {
              field: "Postal code",
              oldValue: currentCompany.postal_code,
              newValue: companyData.postal_code
            }
          });
        }
        
        if (companyData.country !== undefined && currentCompany.country !== companyData.country) {
          await logHistory({
            entityId: companyData.id,
            entityType: "company",
            action: "updated",
            description: "Country",
            change: {
              field: "Country",
              oldValue: currentCompany.country,
              newValue: companyData.country
            }
          });
        }
        
        if (companyData.assignee !== undefined && currentCompany.assignee !== companyData.assignee) {
          await logHistory({
            entityId: companyData.id,
            entityType: "company",
            action: "updated",
            description: "Assignee",
            change: {
              field: "Assignee",
              oldValue: currentCompany.assignee,
              newValue: companyData.assignee
            }
          });
        }
        
        if (companyData.vat_number !== undefined && currentCompany.vat_number !== companyData.vat_number) {
          await logHistory({
            entityId: companyData.id,
            entityType: "company",
            action: "updated",
            description: "VAT number",
            change: {
              field: "VAT number",
              oldValue: currentCompany.vat_number,
              newValue: companyData.vat_number
            }
          });
        }
      }
      
      toast({
        title: "Success",
        description: "Company updated successfully",
      });
      
      getRefreshFunction()(); // Call the refresh function
      return true;
    } catch (error: any) {
      console.error("Error updating company:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update company",
        variant: "destructive",
      });
      return false;
    }
  };

  const handleAddCompany = async (name: string) => {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .insert([{ name }])
        .select('id, name')
        .single();
      
      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Company added successfully",
      });
      
      getRefreshFunction()(); // Call the refresh function
      return data;
    } catch (error: any) {
      console.error("Error adding company:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to add company",
        variant: "destructive",
      });
      return null;
    }
  };

  return {
    handleEditCompany,
    handleAddCompany
  };
};
